import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ExternalLinkIcon } from '../../atoms/icons/ExternalLinkIcon'
import { ProviderCalendarIllustration } from '../../atoms/illustrations/ProviderCalendarIllustration'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Title } from '../../atoms/title/Title'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderCalendarSetupCardProps = {
  onSetupPressed: () => void
  onLearnMorePressed: () => void
  hybridBookingMode?: boolean
  isImpersonating?: boolean
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['56px'],
  borderRadius: '16px',
  border: `1px solid ${theme.colors.borderDefault}`,
  alignSelf: 'flex-start',
  width: '100%',
  alignItems: 'center',
}))

const Content = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['32px'],
  width: '550px',
}))

const Header = styled.View({
  flexDirection: 'column',
  alignItems: 'center',
})

const Row = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  alignSelf: 'center',
  alignItems: 'center',
}))

const LinkButton = styled(TextButton)({
  alignSelf: 'center',
})

const HeaderText = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const InfoMessageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundWarning,
  borderRadius: '8px',
  padding: theme.spacing['16px'],
  marginTop: theme.spacing['16px'],
  width: '100%',
}))

const InfoMessageText = styled(BodyText)(({ theme }) => ({
  color: theme.colors.textPrimary,
}))

export const ProviderCalendarSetupCard: FunctionComponent<ProviderCalendarSetupCardProps> = ({
  onSetupPressed,
  onLearnMorePressed,
  hybridBookingMode,
  isImpersonating = false,
}) => {
  const theme = useTheme() as ThemeType
  return (
    <Container testID={tID('ProviderCalendarSetupCard')}>
      <Content>
        <Header>
          <ProviderCalendarIllustration />
          <HeaderText
            textAlign='center'
            size={TitleSize.MEDIUM}
            text={
              <FormattedMessage
                defaultMessage='Set up your Lyra Calendar'
                description='Title for provider calendar setup card'
              />
            }
          />
          <BodyText
            textAlign='center'
            color={theme.colors.textSecondary}
            text={
              hybridBookingMode ? (
                <FormattedMessage
                  defaultMessage='Manage your schedule and clients in one place'
                  description='Body text for provider calendar setup card'
                />
              ) : (
                <FormattedMessage
                  defaultMessage='Manage your schedule and availability in one place'
                  description='Body text for provider calendar setup card'
                />
              )
            }
          />
        </Header>
        <Row>
          <LinkButton
            testID={tID('ProviderCalendarSetupCard-learn-more-button')}
            rightIcon={<ExternalLinkIcon strokeColor={theme.colors.iconActive} />}
            text={
              hybridBookingMode ? (
                <FormattedMessage
                  defaultMessage='How it works'
                  description='Button to explain the Google calendar integration'
                />
              ) : (
                <FormattedMessage defaultMessage='Learn more' description='Button to learn more about calendar' />
              )
            }
            onPress={onLearnMorePressed}
          />
          <PrimaryButton
            testID={tID('ProviderCalendarSetupCard-setup-button')}
            text={<FormattedMessage defaultMessage='Set up calendar' description='Button to set up calendar' />}
            onPress={onSetupPressed}
            disabled={isImpersonating}
          />
        </Row>
        {isImpersonating && (
          <InfoMessageContainer testID={tID('ProviderCalendarSetupCard-impersonation-message')}>
            <InfoMessageText
              textAlign='center'
              text={
                <FormattedMessage
                  defaultMessage="<bold>Initial setup must be completed in the provider's individual account.</bold> Once their calendar is connected, you can view and manage it here."
                  description='Message shown to practice admin when impersonating a provider, explaining they cannot set up calendar'
                  values={{
                    bold: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                  }}
                />
              }
            />
          </InfoMessageContainer>
        )}
      </Content>
    </Container>
  )
}
